import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import SvgLoading from '@components/SvgLoading';
import { Icon, useModal } from '@ohif/ui';
import { useAppConfig } from '@state';
import HttpClient from '@utils/HttpClient';

import FilterGroup from './FilterGroup';
import FilterButton from './FilterButton';
import { filterConfig } from './config';

const services = [
  { key: 'cxr', label: 'CXR' },
  { key: 'cct', label: 'CCT' },
  { key: 'do', label: 'DXA' },
  // { key: 'hct', label: 'HCT' },
  { key: 'mammo', label: 'MAMMO' },
  { key: 'mskxr', label: 'MSKXR' },
];


function CustomFilters({ t, applyFilter, deleteFilter, filterApplied, hide, renderFilters, handleFavorite }) {
  const [newFilters, setNewFilters] = useState(filterApplied || [])

  function handleFilters(filterId) {
    if (newFilters.includes(filterId)) {
      setNewFilters(newFilters.filter(id => id !== filterId))
    } else {
      setNewFilters([...newFilters, filterId])
    }
  }

  return (
    <div className="flex flex-col min-h-0 flex-1 pt-2">
      <div className="flex-1 overflow-y-auto ohif-scrollbar min-h-0 pr-4">
        {renderFilters.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 gap-2" style={{ color: '#334155' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="opacity-30">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span className="text-xs opacity-40">{t('No filters yet')}</span>
          </div>
        )}

        {renderFilters.map(filter => (
          <div
            key={filter.id}
            className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl transition-all duration-200"
            style={{
              background: newFilters.includes(filter.id)
                ? 'rgba(99,102,241,0.1)'
                : 'rgba(255,255,255,0.03)',
              border: newFilters.includes(filter.id)
                ? '1px solid rgba(99,102,241,0.3)'
                : '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button
              title={filter.favorite ? t('Unset favorite') : t('Set as favorite')}
              onClick={async () => {
                await applyFilter([filter.id])
                const hasFavorite = await handleFavorite(filter.id)
                if (hasFavorite) hide()
              }}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200"
              style={{
                background: filter.favorite ? 'rgba(251,191,36,0.15)' : 'transparent',
                color: filter.favorite ? '#fbbf24' : '#334155',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill={filter.favorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </button>

            <FilterButton
              label={t(filter.name)}
              isActive={newFilters.includes(filter.id)}
              onClick={() => handleFilters(filter.id)}
              className="flex-1 text-left"
            />

            <button
              title={t('Delete filter')}
              type="button"
              onClick={() => deleteFilter(filter.id)}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200"
              style={{ color: '#334155' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.12)';
                (e.currentTarget as HTMLElement).style.color = '#f87171';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
                (e.currentTarget as HTMLElement).style.color = '#334155';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        ))}
      </div>

      {renderFilters.length > 0 && (
        <div className="flex justify-end pt-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              if (!newFilters.length) {
                toast.error(t('Please select one filter'))
                return
              }
              applyFilter(newFilters)
              hide()
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_4px_20px_rgba(99,102,241,0.5)] hover:translate-y-[-1px] active:translate-y-[0]"
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
            }}
          >
            <Icon className="w-3.5 h-3.5" name="check" />
            {t('Apply')}
          </button>
        </div>
      )}
    </div>
  )
}


export default function AdvancedFilters({ applyFilter, customFilters, setCustomFilters }) {
  const { hide } = useModal();
  const { t } = useTranslation('AdvancedFilters');
  const cookies = new Cookies(null, { path: '/' });
  const { appliedFilters, filterApplied, setFilterApplied, setFilteredStudies } = useAppConfig();
  const [selectedFilters, setSelectedFilters] = useState(appliedFilters);
  const [renderFilters, setRenderFilters] = useState(customFilters);
  const [filterName, setFilterName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleFilterChange(key: string, value: any) {
    if (selectedFilters[key] === value) {
      const { [key]: _, ...newFilters } = selectedFilters;
      setSelectedFilters(newFilters);
    } else {
      setSelectedFilters({ ...selectedFilters, [key]: value });
    }
  }

  function handleServiceChange(serviceKey: string) {
    if (appliedFilters.srvc_name === serviceKey) {
      setSelectedFilters(appliedFilters)
    } else {
      setSelectedFilters({ 'srvc_name': serviceKey })
    }
  }

  async function saveFilter(event) {
    event.preventDefault()
    setIsSubmitting(true)
    if (!filterName) {
      toast.error(t('Please enter a name'))
      setIsSubmitting(false)
      return
    }
    const formData = {
      name: filterName,
      filters: selectedFilters
    }
    const resp = await HttpClient.post(
      '/custom-filters',
      formData,
      cookies.get('token')
    )
    if (resp.ok) {
      const data = resp.data
      setCustomFilters([...renderFilters, data])
      setRenderFilters([...renderFilters, data])
      setFilterName('')
      toast.success(t('Filter saved'))

      applyFilter([data.id])
      hide()
      setIsSubmitting(false)
    } else {
      toast.error(t('Common:Server Error'))
      setIsSubmitting(false)
    }
  }

  async function deleteFilter(filterId) {
    await HttpClient.delete(`/custom-filters/${filterId}`, cookies.get('token'))
    const newFilters = renderFilters.filter(filter => filter.id !== filterId)
    setCustomFilters(newFilters)
    setRenderFilters(newFilters)
    if (filterApplied.includes(filterId)) {
      setFilterApplied([])
      setFilteredStudies([])
    }
    toast.success(t('Filter deleted'))
  }

  async function handleFavorite(filterId) {
    const { favorite } = renderFilters.find(filter => filter.id === filterId)
    await HttpClient.patch(
      `/custom-filters/${filterId}/set_favorite`,
      { favorite: !favorite },
      cookies.get('token')
    )
    toast.success(t('Favorite updated'))
    const newFilters = renderFilters.map(filter => {
      if (filter.id === filterId) {
        return { ...filter, favorite: !favorite }
      }
      return { ...filter, favorite: false }
    })
    setCustomFilters(newFilters)
    setRenderFilters(newFilters)

    return newFilters.some(filter => filter.favorite)
  }

  return (
    <div className="px-2 lg:px-6 pt-0 w-full h-full flex flex-col md:flex-row gap-0 min-h-0 overflow-hidden">
      <aside className="max-md:max-h-[30vh] w-full md:w-1/3 lg:w-1/4 min-h-0 flex flex-col">
        <div className="flex items-center gap-2 mb-3 flex-shrink-0">
          <div
            className="w-1 h-4 rounded-full"
            style={{ background: 'linear-gradient(180deg, #3b82f6, #8b5cf6)' }}
          />
          <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#818cf8' }}>
            {t('MY FILTERS')}
          </h3>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <CustomFilters
            t={t}
            applyFilter={applyFilter}
            filterApplied={filterApplied}
            hide={hide}
            deleteFilter={deleteFilter}
            renderFilters={renderFilters}
            handleFavorite={handleFavorite}
          />
        </div>
      </aside>

      <div
        className="hidden md:block flex-shrink-0 mx-4"
        style={{
          width: '1px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(99,102,241,0.35) 20%, rgba(99,102,241,0.35) 80%, transparent 100%)',
        }}
      />
      <div className="md:hidden w-full my-4" style={{ height: '1px', background: 'rgba(99,102,241,0.2)' }} />

      <main className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-2 mb-3 flex-shrink-0">
          <div
            className="w-1 h-4 rounded-full"
            style={{ background: 'linear-gradient(180deg, #3b82f6, #8b5cf6)' }}
          />
          <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#818cf8' }}>
            {t('NEW FILTER')}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto ohif-scrollbar pr-4 pb-2">
          <div className="mb-4">
            <div className="mb-4" style={{ height: '1px', background: 'linear-gradient(90deg, rgba(99,102,241,0.4), transparent)' }} />

            <div className="flex flex-col items-start w-full my-3">
              <h3 className="text-base font-semibold text-white mb-3">
                {t('SERVICE')}
              </h3>

              <div className="flex flex-wrap w-full gap-x-3 gap-y-3 justify-around">
                {services.map(service => (
                  <FilterButton
                    key={service.key}
                    label={t(service.label)}
                    isActive={selectedFilters.srvc_name === service.key}
                    onClick={() => handleServiceChange(service.key)}
                    className="transition-all duration-200 text-sm font-semibold rounded-lg px-4 py-2 flex-1 whitespace-nowrap"
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mb-4" style={{ height: '1px', background: 'linear-gradient(90deg, rgba(99,102,241,0.4), transparent)' }} />

          {selectedFilters.srvc_name && filterConfig[selectedFilters.srvc_name]?.map((group, index) => (
            <React.Fragment key={index}>
              <FilterGroup
                t={t}
                title={group.title}
                options={group.options}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                justify={group.justify}
              />
              <div className="my-3" style={{ height: '1px', background: 'linear-gradient(90deg, rgba(99,102,241,0.3), transparent)' }} />
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={saveFilter} className="flex items-center gap-2 mt-4">
          <input
            type="text"
            placeholder={t('Filter name')}
            className="flex-1 outline-none rounded-lg px-3 py-2 text-sm text-white transition-all duration-200"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(99,102,241,0.25)',
              color: '#e2e8f0',
            }}
            value={filterName}
            onChange={e => setFilterName(e.target.value)}
            onFocus={e => {
              e.currentTarget.style.borderColor = 'rgba(99,102,241,0.7)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-200 flex-shrink-0 hover:translate-y-[-1px] active:translate-y-[0]"
            style={{
              background: isSubmitting
                ? 'rgba(99,102,241,0.3)'
                : 'linear-gradient(135deg, #3b82f6, #6366f1)',
              boxShadow: isSubmitting ? 'none' : '0 2px 8px rgba(99,102,241,0.35)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? (
              <SvgLoading />
            ) : (
              <>
                <Icon name="icon-save" className="w-3.5 h-3.5" />
                {t('Save')}
              </>
            )}
          </button>
        </form>
      </main>
    </div>
  );
};
